import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, catchError, map, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { handleError } from '@shared-functions/handle-error.function';
import { WebSocketService } from './websocket.service';

export interface NotificacionInterna {
  id: number;
  entidadOrigenId: number;
  entidadDestinoId: number | null;
  destinoId: number;
  origenId: number | null;
  tipoEvento: string;
  titulo: string;
  mensaje: string | null;
  enlace: string | null;
  leida: boolean;
  fechaLectura: string | null;
  fechaCreacion: string;
}

@Injectable({ providedIn: 'root' })
export class NotificacionesInternasService implements OnDestroy {
  private readonly BASE = `${environment.API_SERVER}/notifications/internas`;
  private readonly destroy$ = new Subject<void>();

  private readonly _count$ = new BehaviorSubject<number>(0);
  readonly count$ = this._count$.asObservable();

  private readonly _nueva$ = new Subject<NotificacionInterna>();
  readonly nueva$ = this._nueva$.asObservable();

  private wsListening = false;

  constructor(
    private readonly http: HttpClient,
    private readonly ws: WebSocketService,
  ) {}

  iniciarEscucha(): void {
    if (this.wsListening) return;
    this.wsListening = true;

    this.ws
      .listen<NotificacionInterna>('notificacion-interna')
      .pipe(takeUntil(this.destroy$))
      .subscribe((notif) => {
        this._nueva$.next(notif);
        this._count$.next(this._count$.value + 1);
      });
  }

  listar(limit = 20, offset = 0): Observable<NotificacionInterna[]> {
    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset);
    return this.http
      .get<NotificacionInterna[]>(this.BASE, { params })
      .pipe(catchError(handleError));
  }

  historial(
    filtros: {
      limit?: number;
      offset?: number;
      leida?: number | null;
      tipoEvento?: string | null;
      fechaDesde?: string | null;
      fechaHasta?: string | null;
    } = {},
  ): Observable<{ items: NotificacionInterna[]; total: number }> {
    let params = new HttpParams();
    if (filtros.limit) params = params.set('limit', filtros.limit);
    if (filtros.offset) params = params.set('offset', filtros.offset);
    if (filtros.leida !== undefined && filtros.leida !== null) params = params.set('leida', filtros.leida);
    if (filtros.tipoEvento) params = params.set('tipoEvento', filtros.tipoEvento);
    if (filtros.fechaDesde) params = params.set('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params = params.set('fechaHasta', filtros.fechaHasta);
    return this.http
      .get<{ items: NotificacionInterna[]; total: number }>(`${this.BASE}/historial`, { params })
      .pipe(catchError(handleError));
  }

  refrescarConteo(): void {
    this.http
      .get<{ count: number }>(`${this.BASE}/count`)
      .pipe(catchError(handleError))
      .subscribe((res) => this._count$.next(res.count));
  }

  marcarLeida(id: number): Observable<{ ok: boolean }> {
    return this.http
      .patch<{ ok: boolean }>(`${this.BASE}/${id}/read`, {})
      .pipe(
        tap(() => {
          const c = this._count$.value;
          if (c > 0) this._count$.next(c - 1);
        }),
        catchError(handleError),
      );
  }

  marcarTodasLeidas(): Observable<{ ok: boolean }> {
    return this.http
      .patch<{ ok: boolean }>(`${this.BASE}/read-all`, {})
      .pipe(
        tap(() => this._count$.next(0)),
        catchError(handleError),
      );
  }

  eliminar(id: number): Observable<{ ok: boolean }> {
    return this.http
      .delete<{ ok: boolean }>(`${this.BASE}/${id}`)
      .pipe(catchError(handleError));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
